import React, { useState, useEffect, useRef } from "react";
import { Button, Input, Space, Table, message } from "antd";
import {
  SearchOutlined,
  ExpandAltOutlined,
} from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import SuspensionHistoryDialog from "./suspensionHistoryModal";
import Stats from "../tester-teamlead/tester-stats";
import "./blockedTestersV2.css";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const apiUrl = process.env.REACT_APP_BlockedTestersList;
const suspensionHistoryApiUrl = process.env.REACT_APP_SuspensionHistory;
const toggleUrl = process.env.REACT_APP_AccountStatusToggle;

const formatLastLogin = (timeString) => {
  if (!timeString) return "";
  if (timeString === "Haven't logged in yet") return "Haven't logged in yet";
  const matches = timeString.match(/\d+/g);
  if (!matches) return timeString;

  let days = 0, hours = 0, minutes = 0;
  if (timeString.includes("day")) {
    days = matches[0] ? parseInt(matches[0], 10) : 0;
    if (timeString.includes("hour")) hours = matches[1] ? parseInt(matches[1], 10) : 0;
  } else if (timeString.includes("hour")) {
    hours = matches[0] ? parseInt(matches[0], 10) : 0;
    if (timeString.includes("minute")) minutes = matches[1] ? parseInt(matches[1], 10) : 0;
  } else if (timeString.includes("minute")) {
    minutes = matches[0] ? parseInt(matches[0], 10) : 0;
  }

  const totalHours = hours + minutes / 60;
  days += Math.floor(totalHours / 24);
  hours = Math.floor(totalHours % 24);

  const daysInYear = 365.25;
  const daysInMonth = 30.44;
  const years = Math.floor(days / daysInYear);
  const remainingDaysAfterYears = days % daysInYear;
  const months = Math.floor(remainingDaysAfterYears / daysInMonth);
  const remainingDays = Math.floor(remainingDaysAfterYears % daysInMonth);

  let result = "";
  if (years > 0) result += `${years} year${years > 1 ? "s" : ""} `;
  if (months > 0) result += `${months} month${months > 1 ? "s" : ""} `;
  if (remainingDays > 0) result += `${remainingDays} day${remainingDays > 1 ? "s" : ""} `;
  if (hours > 0) result += `${hours} hour${hours !== 1 ? "s" : ""} `;
  if (minutes > 0) result += `${minutes} minute${minutes !== 1 ? "s" : ""}`;

  return result.trim() || "0 minutes";
};

const calculateRemainingHours = (inactivatedAt) => {
  if (!inactivatedAt) return 0;
  const currentTime = new Date();
  const inactivatedTime = new Date(inactivatedAt);
  const timeDifferenceInMs = currentTime - inactivatedTime;
  const hoursPassed = timeDifferenceInMs / (1000 * 60 * 60);
  const remainingHours = 72 - hoursPassed;
  return remainingHours > 0 ? Math.floor(remainingHours) : 0;
};

const BlockedTestersV2 = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [testers, setTesters] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [selectedTester, setSelectedTester] = useState(null);
  const [searchedColumn, setSearchedColumn] = useState("");
  const [suspensionHistory, setSuspensionHistory] = useState([]);
  const [suspensionCount, setSuspensionCount] = useState(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingId, setLoadingId] = useState(null);
  const searchInput = useRef(null);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await fetch(apiUrl);
      if (!response.ok) throw new Error("Failed to fetch blocked testers");
      const data = await response.json();
      const formattedData = (data.body || []).map((tester) => ({
        ...tester,
        last_login: formatLastLogin(tester.last_login),
      }));
      setTesters(formattedData);
    } catch (error) {
      console.error("Error fetching blocked testers:", error);
    } finally {
      setLoading(false);
    }
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div className="blocked-v2-filter" onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
        />
        <Space className="blocked-v2-filter-actions">
          <Button
            type="primary"
            size="small"
            icon={<SearchOutlined />}
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          >
            Search
          </Button>
          <Button size="small" onClick={() => clearFilters && handleReset(clearFilters)}>
            Reset
          </Button>
          <Button type="link" size="small" onClick={() => close()}>
            Close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined className={filtered ? "blocked-v2-filter-active" : ""} />
    ),
    onFilter: (value, record) =>
      (record[dataIndex]?.toString() || "").toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) setTimeout(() => searchInput.current?.select(), 100);
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "rgba(56, 189, 248, 0.3)", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text != null ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const handleViewClick = (record) => {
    setSelectedTester(record);
    setStatsDialogOpen(true);
  };

  const handleSuspensionCountClick = async (record) => {
    try {
      const response = await fetch(`${suspensionHistoryApiUrl}/${record.id}`);
      const data = await response.json();
      const parsedData = JSON.parse(data.body);
      setSuspensionHistory(parsedData.history);
      setSuspensionCount(parsedData.history.length);
      setIsDialogOpen(true);
    } catch (error) {
      console.error("Failed to fetch suspension history", error);
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      if (!toggleUrl) throw new Error("Toggle URL is not defined");
      setLoadingId(id);

      const updatedStatus = currentStatus === 0 ? 1 : 0;
      const response = await fetch(toggleUrl, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: id }),
      });

      if (!response.ok) throw new Error("Failed to toggle status");

      fetchData();
      messageApi.success(
        `User ${updatedStatus === 0 ? "activated" : "inactivated"} successfully!`
      );
    } catch (error) {
      messageApi.error("Failed to toggle status");
    } finally {
      setLoadingId(null);
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "first_name",
      key: "first_name",
      width: "14%",
      ...getColumnSearchProps("first_name"),
      sorter: (a, b) => (a.first_name || "").localeCompare(b.first_name || ""),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: "18%",
      ...getColumnSearchProps("email"),
      sorter: (a, b) => (a.email || "").localeCompare(b.email || ""),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Last Login",
      dataIndex: "last_login",
      key: "last_login",
      width: "14%",
      ...getColumnSearchProps("last_login"),
      sorter: (a, b) => (a.last_login || "").toString().localeCompare((b.last_login || "").toString()),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Suspension",
      dataIndex: "suspension_count",
      key: "suspension_count",
      width: "10%",
      sorter: (a, b) => (a.suspension_count || 0) - (b.suspension_count || 0),
      sortDirections: ["descend", "ascend"],
      render: (text, record) =>
        text > 0 ? (
          <Button type="link" onClick={() => handleSuspensionCountClick(record)} className="blocked-v2-suspension-link">
            {text}
          </Button>
        ) : (
          <span>{text}</span>
        ),
    },
    {
      title: "Inactivated at",
      dataIndex: "inactivated_at",
      key: "inactivated_at",
      width: "14%",
      ...getColumnSearchProps("inactivated_at"),
      sorter: (a, b) => (a.inactivated_at || "").localeCompare(b.inactivated_at || ""),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Remaining Time",
      dataIndex: "inactivated_at",
      key: "remaining_time",
      width: "12%",
      render: (inactivatedAt) => {
        const remainingHours = calculateRemainingHours(inactivatedAt);
        return `${remainingHours} hour${remainingHours !== 1 ? "s" : ""} left`;
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: "18%",
      render: (_, record) => (
        <div className="blocked-v2-actions">
          <Button
            type="default"
            icon={<ExpandAltOutlined />}
            onClick={() => handleViewClick(record)}
            className="blocked-v2-btn-view"
          >
            View
          </Button>
          <Button
            type={record.inactive === 0 ? "primary" : "default"}
            danger={record.inactive === 0}
            onClick={() => handleToggleStatus(record.id, record.inactive)}
            disabled={loadingId === record.id}
            loading={loadingId === record.id}
            className="blocked-v2-btn-toggle"
          >
            {record.inactive === 0 ? "Inactivate" : "Activate"}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="blocked-v2">
      {contextHolder}
      <div className="blocked-v2-header">
        <h1 className="blocked-v2-title">Blocked Testers</h1>
      </div>

      <div className="blocked-v2-card">
        <Table
          columns={columns}
          dataSource={testers}
          loading={loading}
          rowKey="id"
          locale={{ emptyText: loading ? "Loading..." : "No blocked testers" }}
          pagination={{
            showSizeChanger: true,
            defaultPageSize: 10,
            pageSizeOptions: ["5", "10", "20", "50"],
            showTotal: (total) => `Total ${total} blocked testers`,
          }}
          className="blocked-v2-table"
          scroll={{ x: "max-content" }}
        />
      </div>

      {/* Stats Dialog */}
      <Dialog
        open={statsDialogOpen}
        onClose={() => setStatsDialogOpen(false)}
        TransitionComponent={Transition}
        maxWidth="md"
        fullWidth
        className="blocked-v2-dialog"
        PaperProps={{
          sx: {
            borderRadius: 8,
            boxShadow: "0 24px 48px rgba(0, 0, 0, 0.12)",
            overflow: "hidden",
            maxHeight: "90vh",
          },
        }}
      >
        <AppBar className="blocked-v2-stats-appbar" position="relative">
          <Toolbar>
            <Typography variant="h6" component="div" sx={{ flex: 1 }}>
              Tester Statistics
            </Typography>
            {selectedTester && (
              <Typography variant="body2" sx={{ mr: 2, opacity: 0.9 }}>
                {selectedTester.first_name} {selectedTester.last_name}
              </Typography>
            )}
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => setStatsDialogOpen(false)}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <div className="blocked-v2-stats-body">
          {selectedTester && <Stats id={selectedTester.id} />}
        </div>
      </Dialog>

      <SuspensionHistoryDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        suspensionHistory={suspensionHistory}
        suspensionCount={suspensionCount}
      />
    </div>
  );
};

export default BlockedTestersV2;
